/**
 * Search Query Builder for Supabase
 * Supports: union (+, |), intersection (&), subtraction (-)
 */

export interface ParsedSearchQuery {
  type: 'simple' | 'union' | 'intersection';
  keywords: string[];
  excluded: string[];
}

/**
 * Escape special characters for Supabase .or() filter syntax
 * Uses double-quote wrapping per PostgREST docs for reserved chars: , . : ()
 */
export const escapeSupabaseFilter = (term: string): string => {
  if (!term) return '';
  
  // Escape double quotes inside the term, then wrap in double quotes
  // PostgREST treats quoted strings as literals
  const escapedQuotes = term.replace(/"/g, '\\"');
  return `"${escapedQuotes}"`;
};

/**
 * Parse user search input into structured query
 * 
 * Examples:
 * - "chair" → simple search
 * - "chair + ghế" or "chair | ghế" → union (OR)
 * - "chair & wood" → intersection (AND)
 * - "chair - plastic" → simple with exclusion
 * - "chair + ghế - plastic" → union with exclusion
 */
export const parseSearchQuery = (input: string): ParsedSearchQuery => {
  if (!input || typeof input !== 'string') {
    return { type: 'simple', keywords: [], excluded: [] };
  }

  const trimmed = input.trim();
  if (!trimmed) {
    return { type: 'simple', keywords: [], excluded: [] };
  }

  // Check for exclusion terms first (terms after -)
  const excluded: string[] = [];
  let mainPart = trimmed;

  // Extract excluded terms (- keyword)
  const exclusionMatches = trimmed.match(/\s+-\s+(\S+)/g);
  if (exclusionMatches) {
    exclusionMatches.forEach(match => {
      const term = match.replace(/\s+-\s+/, '').trim();
      if (term) excluded.push(term);
    });
    // Remove exclusion parts from main query
    mainPart = trimmed.replace(/\s+-\s+\S+/g, '').trim();
  }

  // Check for intersection operator (&)
  if (mainPart.includes(' & ')) {
    const keywords = mainPart
      .split(/\s+&\s+/)
      .map(k => k.trim())
      .filter(k => k.length > 0);
    return { type: 'intersection', keywords, excluded };
  }

  // Check for union operator (+ or |)
  if (mainPart.includes(' + ') || mainPart.includes(' | ')) {
    const keywords = mainPart
      .split(/\s+[+|]\s+/)
      .map(k => k.trim())
      .filter(k => k.length > 0);
    return { type: 'union', keywords, excluded };
  }

  // Simple search
  return { 
    type: 'simple', 
    keywords: mainPart ? [mainPart] : [], 
    excluded 
  };
};

/**
 * Build a single keyword filter for multiple columns
 * Wildcards must be INSIDE the double quotes for PostgREST
 */
const buildKeywordFilter = (keyword: string, columns: string[]): string => {
  if (!keyword) return '';
  
  // Escape quotes, wrap wildcards INSIDE quotes for PostgREST literal matching
  const escapedQuotes = keyword.replace(/"/g, '\\"');
  const pattern = `"%${escapedQuotes}%"`;
  return columns.map(col => `${col}.ilike.${pattern}`).join(',');
};

/**
 * Build Supabase filter based on parsed query
 * Returns filter string for use with .or() or null if no keywords
 */
export const buildSupabaseSearchFilter = (
  parsed: ParsedSearchQuery,
  columns: string[]
): { include: string | null; exclude: string[] } => {
  if (parsed.keywords.length === 0) {
    return { include: null, exclude: [] };
  }

  let includeFilter: string | null = null;

  if (parsed.type === 'simple' || parsed.type === 'union') {
    // OR logic: any keyword matches any column
    const allFilters = parsed.keywords.map(kw => buildKeywordFilter(kw, columns));
    includeFilter = allFilters.join(',');
  } else if (parsed.type === 'intersection') {
    // AND logic: all keywords must match (handled differently - return first keyword for now)
    // Note: Supabase .or() doesn't support AND, so we return first keyword
    // and handle intersection in post-processing or multiple queries
    includeFilter = buildKeywordFilter(parsed.keywords[0], columns);
  }

  // Exclusion terms are used for client-side JavaScript matching (shouldExcludeItem)
  // No PostgREST escaping needed - keep original terms
  return { include: includeFilter, exclude: parsed.excluded };
};

/**
 * Check if an item should be excluded based on excluded terms
 */
export const shouldExcludeItem = (
  item: Record<string, unknown>,
  excludedTerms: string[],
  columns: string[]
): boolean => {
  if (excludedTerms.length === 0) return false;

  const lowerExcluded = excludedTerms.map(t => t.toLowerCase());

  for (const col of columns) {
    const value = item[col];
    if (typeof value === 'string') {
      const lowerValue = value.toLowerCase();
      for (const term of lowerExcluded) {
        if (lowerValue.includes(term)) {
          return true;
        }
      }
    }
  }

  return false;
};

/**
 * Check if an item matches ALL keywords (for intersection)
 */
export const matchesAllKeywords = (
  item: Record<string, unknown>,
  keywords: string[],
  columns: string[]
): boolean => {
  if (keywords.length === 0) return true;

  const lowerKeywords = keywords.map(k => k.toLowerCase());

  return lowerKeywords.every(keyword => {
    return columns.some(col => {
      const value = item[col];
      if (typeof value === 'string') {
        return value.toLowerCase().includes(keyword);
      }
      return false;
    });
  });
};
